import {
  Checkbox, FormControl,
  Input, InputLabel,
  ListItemText,
  MenuItem,
  Select
} from '@material-ui/core'
import withStyles from '@material-ui/core/styles/withStyles'
import Check from '@material-ui/icons/Check'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import 'react-phone-number-input/style.css'
import formStyle from '../../../assets-soapy/jss/soapy/components/formStyle.jsx'
import Card from '../../../components-soapy/Card/Card.jsx'
import LoadingButton from '../../../components-soapy/LoadingButton.jsx'
import CardBody from '../../../components/Card/CardBody.jsx'
import CustomInput from '../../../components/CustomInput/CustomInput.jsx'
import GridContainer from '../../../components/Grid/GridContainer.jsx'
import GridItem from '../../../components/Grid/GridItem.jsx'
import withAuthentication from '../../../containers/Authentication/withAuthentication.js'
import withErrorNotification from '../../../containers/withErrorNotification.js'
import { addOrganization } from '../../../endpoint-requests/organization/addOrganization.js'
import { editOrganization } from '../../../endpoint-requests/organization/editOrganization.js'
import useFormFields from '../../../shared/hooks/useFormFields.js'
import validateFormFields from '../../../utils/validateFormFields.js'
import { isEmail, verifyLength } from '../../../utils/validators'

const INFO_COLOR = '#00acc1'

const NAME = 'name'
const COUNTRY = 'country'
const LATITUDE = 'latitude'
const LONGITUDE = 'longitude'
const STATE = 'state'
const CITY = 'city'
const DISTRICT = 'district'
const ADDRESS = 'address'
const ZIP_CODE = 'zipcode'
const CONTACT_NAME = 'contactName'
const DISTRIBUTOR_ID = 'distributorId'
const CONTACT_EMAIL = 'contactEmail'
const WEBSITE = 'website'
const VALIDATION_STATE = 'ValidationState'
const STATUS = 'status'


let spinnerTimeout = null

const EditOrganization = ({
  classes,
  showErrorNotification,
  organizationAdded,
  currentDashboardUser,
  organizationEdited,
  organizations,
  organization,
  distributors,
  t
}) => {
  const isEdit = !!Object.keys(organization).length
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false)
  const { formFields, setFormFields } = useFormFields({
    [NAME]: {
      value: '',
      isRequired: true,
      name: 'Name',
      validators: [
        {
          isValid: (value) => verifyLength(value, 1, 256),
          message: 'Name should not be empty, and up to 256 characters'
        }
      ]
    },
    [CONTACT_EMAIL]: {
      value: '',
      isRequired: false,
      name: 'Contact email',
      validators: [
        {
          isValid: (value) => isEmail(value),
          message: 'Email is not valid'
        }
      ]
    },
    [COUNTRY]: { value: '', isRequired: false, name: 'Country' },
    [STATE]: { value: '', isRequired: false, name: 'State' },
    [CITY]: { value: '', isRequired: false, name: 'City' },
    [ZIP_CODE]: { value: '', isRequired: false, name: 'Zip code' },
    [LONGITUDE]: { value: '', isRequired: false, name: 'Latitude' },
    [LATITUDE]: { value: '', isRequired: false, name: 'Longitude' },
    [DISTRICT]: { value: '', isRequired: false, name: 'District' },
    [ADDRESS]: { value: '', isRequired: false, name: 'Address' },
    [WEBSITE]: { value: '', isRequired: false, name: 'Website' },
    [DISTRIBUTOR_ID]: { value: '', isRequired: () => currentDashboardUser.entity.name === 'neo', name: 'Distributor' },
    [CONTACT_NAME]: { value: '', isRequired: false, name: 'Contact name' },
    [STATUS]: { value: '', isRequired: true, name: 'Status' },

  })

  const submitForm = async () => {
    const organizationRequestObject = {
      name: formFields[NAME].value,
      country: formFields[COUNTRY].value,
      state: formFields[STATE].value,
      contactName: formFields[CONTACT_NAME].value,
      contactEmail: formFields[CONTACT_EMAIL].value,
      distributorId: formFields[DISTRIBUTOR_ID].value,
      website: formFields[WEBSITE].value,
      city: formFields[CITY].value,
      district: formFields[DISTRICT].value,
      address: formFields[ADDRESS].value,
      zipcode: formFields[ZIP_CODE].value,
      status: formFields[STATUS].value

    }

    activateSpinner()
    try {
      if (!isEdit) {
        const createdSite = await addOrganization(organizationRequestObject)
        organizationAdded(createdSite)
      } else {
        const editedSite = await editOrganization(organization.id, organizationRequestObject)
        organizationEdited(editedSite)
      }
    } catch (err) {
      showErrorNotification(err.message)
    }
    disableSpinner()
  }

  const activateSpinner = () => {
    spinnerTimeout = setTimeout(
      function () {
        setIsAwaitingResponse(true)
      },
      300
    )
  }
  const disableSpinner = () => {
    clearTimeout(spinnerTimeout)
    setIsAwaitingResponse(false)
  }

  const changeValidationStateToError = affectedFieldsNames => {
    affectedFieldsNames.forEach(fieldName => {
      setFormFields({ ...formFields, [fieldName + VALIDATION_STATE]: 'error' })
    })
  }

  const processForm = async () => {
    const { isValid, error } = await validateFormFields(formFields)
    if (isValid) {
      submitForm()
    } else {
      showErrorNotification(error.message)
      changeValidationStateToError(error.affectedFieldsNames)
    }
  }

  const handleSaveBtnClick = () => {
    processForm()
  }

  useEffect(() => {
    setFormFields(
      (formFields) =>
      ({
        [NAME]: { ...formFields[NAME], value: organization[NAME] || '' },
        [COUNTRY]: { ...formFields[COUNTRY], value: organization[COUNTRY] || '' },
        [STATE]: { ...formFields[STATE], value: organization[STATE] || '' },
        [CITY]: { ...formFields[CITY], value: organization[CITY] || '' },
        [ZIP_CODE]: { ...formFields[ZIP_CODE], value: organization[ZIP_CODE] || '' },
        [LONGITUDE]: { ...formFields[LONGITUDE], value: organization[LONGITUDE] || '' },
        [LATITUDE]: { ...formFields[LATITUDE], value: organization[LATITUDE] || '' },
        [DISTRICT]: { ...formFields[DISTRICT], value: organization[DISTRICT] || '' },
        [CONTACT_EMAIL]: { ...formFields[CONTACT_EMAIL], value: organization[CONTACT_EMAIL] || '' },
        [CONTACT_NAME]: { ...formFields[CONTACT_NAME], value: organization[CONTACT_NAME] || '' },
        [WEBSITE]: { ...formFields[WEBSITE], value: organization[WEBSITE] || '' },
        [ADDRESS]: { ...formFields[ADDRESS], value: organization[ADDRESS] || '' },
        [DISTRIBUTOR_ID]: { ...formFields[DISTRIBUTOR_ID], value: currentDashboardUser.entity.name === 'neo' ? organization[DISTRIBUTOR_ID] : currentDashboardUser.distributorId },
        [STATUS]: { ...formFields[STATUS], value: organization[STATUS] || 'active' },
      })
    )
  }, [organization]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      clearTimeout(spinnerTimeout)
      spinnerTimeout = null
    }
  }, [])

  const handleTextChange = (event, fieldName) => {
    setFormFields({ ...formFields, [fieldName]: { ...formFields[fieldName], value: event.target.value } })
  }
  return (
    <GridContainer id='content-pane-layout' justify='center'>
      <GridItem md={9}>
        <Card style={{ boxShadow: 'none' }}>
          <CardBody>
            <div>
              <form>
                <GridContainer
                  direction='column'
                  justify='center'
                  alignItems='center'
                  id='card-body-section'
                >
                  <GridItem style={{ minWidth: '40vh' }}>
                    <CustomInput
                      id='organizationName'
                      error={formFields[NAME + VALIDATION_STATE]}
                      labelText={`${t('Name')} *`}
                      inputProps={{
                        value: formFields[NAME].value,
                        type: 'name',
                        onChange: event => {
                          handleTextChange(event, NAME)
                        }
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>
                  <GridItem style={{ minWidth: '40vh' }}>
                    <CustomInput
                      id='organizationCountry'
                      labelText={t('Country')}
                      inputProps={{
                        value: formFields[COUNTRY].value,
                        type: 'name',
                        onChange: event => {
                          handleTextChange(event, COUNTRY)
                        }
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>
                  <GridItem style={{ minWidth: '40vh' }}>
                    <CustomInput
                      labelText={t('State')}
                      id='organizationState'
                      inputProps={{
                        value: formFields[STATE].value,
                        type: 'name',
                        onChange: event => {
                          handleTextChange(event, STATE)
                        }
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>
                  <GridItem style={{ minWidth: '40vh' }}>
                    <CustomInput
                      labelText={t('City')}
                      id='organizationCity'
                      inputProps={{
                        value: formFields[CITY].value,
                        onChange: event => {
                          handleTextChange(event, CITY)
                        }
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>
                  <GridItem style={{ minWidth: '40vh' }}>
                    <CustomInput
                      labelText={t('District')}
                      id='organizationDistrict'
                      inputProps={{
                        value: formFields[DISTRICT].value,
                        onChange: event => {
                          handleTextChange(event, DISTRICT)
                        }
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>
                  <GridItem style={{ minWidth: '40vh' }}>
                    <CustomInput
                      labelText={t('Address')}
                      id='organizationAddress'
                      inputProps={{
                        value: formFields[ADDRESS].value,
                        onChange: event => {
                          handleTextChange(event, ADDRESS)
                        }
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>
                  <GridItem style={{ minWidth: '40vh' }}>
                    <CustomInput
                      id='organizationZipCode'
                      labelText={t('Zip code')}
                      inputProps={{
                        value: formFields[ZIP_CODE].value,
                        onChange: event => {
                          handleTextChange(event, ZIP_CODE)
                        }
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>

                  <GridItem style={{ minWidth: '40vh' }}>
                    <CustomInput
                      labelText={t('Contact name')}
                      id='organizationContactName'
                      inputProps={{
                        value: formFields[CONTACT_NAME].value,
                        onChange: event => {
                          handleTextChange(event, CONTACT_NAME)
                        }
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>
                  <GridItem style={{ minWidth: '40vh' }}>
                    <CustomInput
                      labelText={t('Contact email')}
                      id='organizationContactEmail'
                      inputProps={{
                        value: formFields[CONTACT_EMAIL].value,
                        onChange: event => {
                          handleTextChange(event, CONTACT_EMAIL)
                        }
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>
                  <GridItem style={{ minWidth: '40vh' }}>
                    <CustomInput
                      labelText={t('Website')}
                      id='organizationWebsite'
                      inputProps={{
                        value: formFields[WEBSITE].value,
                        onChange: event => {
                          handleTextChange(event, WEBSITE)
                        }
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>
                  {currentDashboardUser.entity.name === 'neo'
                    ? <GridItem style={{
                      width: '40vh',
                      margin: '0 0 17px 0',
                      paddingTop: '27px',
                      position: 'relative',
                      verticalAlign: 'unset'
                    }}
                    >
                      <FormControl fullWidth>
                        <InputLabel
                          style={{
                            color: '#aaaaaa',
                            fontSize: '14px',
                            fontWeight: 400
                          }}
                          id='editSiteDistributor'
                        >
                          {`${t('Distributor')} *`}
                        </InputLabel>
                        <Select
                          error={formFields[DISTRIBUTOR_ID + VALIDATION_STATE]}
                          style={{
                            fontSize: '14px',
                            fontWeight: 400,
                            color: '#495057'
                          }}
                          id='editSiteDistributorSelect'
                          input={<Input />}
                          renderValue={(selected) => distributors[formFields[DISTRIBUTOR_ID].value]}
                          value={formFields[DISTRIBUTOR_ID].value}
                          MenuProps={{
                            className: classes.selectMenu
                          }}
                          inputProps={{
                            onChange: event => {
                              handleTextChange(event, DISTRIBUTOR_ID)
                            }
                          }}
                        >
                          {
                            Object.keys(distributors).map(item => {
                              return (
                                <MenuItem
                                  id='editSiteDistributorSelectMenuItem'
                                  classes={{
                                    root: classes.selectMenuItem,
                                    selected: classes.selectMenuItemSelected
                                  }}
                                  key={item}
                                  value={item}
                                >
                                  <Checkbox
                                    id='editSiteDistributorSelectCheckbox'
                                    checkedIcon={<Check
                                      className={classes.checkedIcon}
                                      style={{ color: INFO_COLOR }}
                                    />}
                                    icon={<Check
                                      className={classes.uncheckedIcon}
                                    />}
                                    checked={formFields[DISTRIBUTOR_ID].value === item}
                                  />
                                  <ListItemText primary={distributors[item]} />
                                </MenuItem>
                              )
                            })
                          }
                        </Select>
                      </FormControl>
                    </GridItem>
                    : null}
                  <GridItem style={{
                    width: '40vh',
                    margin: '0 0 17px 0',
                    paddingTop: '27px',
                    position: 'relative',
                    verticalAlign: 'unset'
                  }}
                  >
                    <FormControl variant="standard" fullWidth>
                      <InputLabel id="demo-simple-select-standard-label">Status</InputLabel>
                      <Select
                        labelId="demo-simple-select-standard-label"
                        id="demo-simple-select-standard"
                        value={formFields[STATUS].value}
                        MenuProps={{
                          className: classes.selectMenu
                        }}
                        inputProps={{
                          onChange: event => {
                            handleTextChange(event, STATUS);
                          }
                        }}
                        label="Status"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                      </Select>
                    </FormControl>
                  </GridItem>
                </GridContainer>
                <GridContainer
                  justify='center'
                  alignItems='center'
                  id='card-footer-section'
                >
                  <GridItem>
                    <LoadingButton
                      color='info'
                      type='submit'
                      id='siteEditSaveButton'
                      onClick={(e) => {
                        e.preventDefault()
                        handleSaveBtnClick()
                      }}
                      fullWidth
                      disabled={isAwaitingResponse}
                      isLoading={isAwaitingResponse}
                    >
                      {t('Save')}
                    </LoadingButton>
                  </GridItem>
                </GridContainer>
              </form>

            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  )
}

EditOrganization.propTypes = {
  classes: PropTypes.object.isRequired
}

export default withErrorNotification(withAuthentication(withStyles(formStyle)(withTranslation()(EditOrganization))))
